/* Introcard storefront behaviour: pre-order overlay, reveal-on-scroll.
   No dependencies. Progressive enhancement: every trigger is a real link. */
(() => {
  'use strict';

  const dialog = document.getElementById('ic-preorder');
  const root = document.documentElement;
  let opener = null;

  /* ---------- Overlay ---------- */

  function openOverlay(trigger) {
    if (!dialog || dialog.open) return;
    opener = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    root.classList.add('ic-lock');
    /* Fine pointers go straight to the email field; on touch screens focus the
       dialog's close button so the keyboard does not cover the steps. */
    const fine = window.matchMedia('(pointer: fine)').matches;
    const target = fine ? dialog.querySelector('input[type="email"]') : dialog.querySelector('[data-ic-close]');
    if (target) requestAnimationFrame(() => target.focus({ preventScroll: true }));
    if (window.history && location.hash !== '#pre-order') {
      history.replaceState(null, '', '#pre-order');
    }
  }

  function closeOverlay() {
    if (!dialog) return;
    if (dialog.open && typeof dialog.close === 'function') dialog.close();
    dialog.removeAttribute('open');
    root.classList.remove('ic-lock');
    if (location.hash === '#pre-order' && window.history) {
      history.replaceState(null, '', location.pathname + location.search);
    }
    if (opener && typeof opener.focus === 'function') opener.focus();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ic-preorder]');
    if (trigger && dialog) {
      event.preventDefault();
      openOverlay(trigger);
      return;
    }
    if (event.target.closest('[data-ic-close]')) {
      event.preventDefault();
      closeOverlay();
    }
  });

  if (dialog) {
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeOverlay(); });
    dialog.addEventListener('close', () => root.classList.remove('ic-lock'));
    if (location.hash === '#pre-order') openOverlay(null);
  }

  /* ---------- Pre-order form ---------- */

  const form = dialog && dialog.querySelector('[data-ic-preorder-form]');

  if (form) {
    const email = form.querySelector('input[type="email"]');
    const consent = form.querySelector('input[name="consent"]');
    const error = form.querySelector('.ic-form-error');
    const status = form.querySelector('[data-ic-status]');
    const submit = form.querySelector('[data-ic-submit]');
    const variantId = Number(dialog.dataset.variantId);
    const dispatch = dialog.dataset.dispatch || '';
    const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    try {
      const saved = sessionStorage.getItem('ic_email');
      if (saved && !email.value) email.value = saved;
    } catch (_) { /* storage unavailable */ }

    email.addEventListener('input', () => {
      if (email.getAttribute('aria-invalid') === 'true' && EMAIL.test(email.value.trim())) {
        email.removeAttribute('aria-invalid');
        error.textContent = '';
      }
    });

    const post = (url, body) => fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const value = email.value.trim();

      if (!EMAIL.test(value)) {
        email.setAttribute('aria-invalid', 'true');
        error.textContent = 'Enter a valid email address, for example name@company.com.';
        email.focus();
        return;
      }

      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      status.textContent = 'Opening secure checkout…';

      try { sessionStorage.setItem('ic_email', value); } catch (_) { /* ignore */ }

      try {
        /* 1. Marketing consent: only when the box was ticked. Shopify's customer form
              subscribes the email to marketing, so it must never run without consent. */
        if (consent && consent.checked) {
          const data = new FormData();
          data.append('form_type', 'customer');
          data.append('utf8', '✓');
          data.append('contact[email]', value);
          data.append('contact[tags]', 'founding-preorder-overlay');
          await fetch('/contact', { method: 'POST', body: data, credentials: 'same-origin' }).catch(() => {});
        }

        /* 2. One founding card per checkout, labelled as a pre-order everywhere it appears. */
        await post('/cart/clear.js');
        const added = await post('/cart/add.js', {
          items: [{
            id: variantId,
            quantity: 1,
            properties: { 'Pre-order': `Estimated dispatch ${dispatch}` },
          }],
        });

        if (!added.ok) {
          const body = await added.json().catch(() => ({}));
          throw new Error(body.description || 'The founding edition may have just sold out. Please refresh the page.');
        }

        await post('/cart/update.js', { attributes: { 'Order type': 'Founding edition pre-order' } });

        /* 3. Shopify checkout with the email already filled in. */
        const params = new URLSearchParams();
        params.set('checkout[email]', value);
        window.location.assign(`/checkout?${params.toString()}`);
      } catch (err) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        status.textContent = '';
        error.textContent = err && err.message ? err.message : 'Something went wrong. Please try again.';
      }
    });
  }

  /* ---------- Cookie settings (Shopify customer privacy banner) ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ic-privacy-settings]');
    if (!btn) return;
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      window.privacyBanner.showPreferences();
    } else if (btn.dataset.fallback) {
      window.location.assign(btn.dataset.fallback);
    }
  });

  /* ---------- Motion: reveal, stock bars, smooth scroll, scroll-linked effects ---------- */

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canObserve = 'IntersectionObserver' in window;

  // Stagger siblings that reveal together.
  document.querySelectorAll('.ic-reveal').forEach((el) => {
    const group = [...el.parentElement.children].filter((c) => c.classList.contains('ic-reveal'));
    el.style.setProperty('--i', String(group.indexOf(el)));
  });

  const revealTargets = document.querySelectorAll('.ic-reveal, .ic-stock, .ic-dash');

  if (reduce || !canObserve) {
    revealTargets.forEach((el) => el.classList.add('is-in'));
    document.querySelectorAll('[data-ic-hero]').forEach((el) => el.classList.add('is-loaded'));
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      if (entry.target.classList.contains('ic-dash')) countUp(entry.target);
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  revealTargets.forEach((el) => io.observe(el));

  // Hero entrance once the hero image is ready (or after a short wait).
  document.querySelectorAll('[data-ic-hero]').forEach((hero) => {
    const img = hero.querySelector('.ic-hero__media img');
    const go = () => requestAnimationFrame(() => hero.classList.add('is-loaded'));
    if (!img || img.complete) go();
    else { img.addEventListener('load', go, { once: true }); setTimeout(go, 900); }
  });

  // Dashboard figures count up from zero.
  function countUp(scope) {
    scope.querySelectorAll('[data-ic-count]').forEach((el) => {
      const text = el.textContent.trim();
      const target = parseFloat(text.replace(/[^0-9.]/g, ''));
      if (!isFinite(target)) return;
      const decimals = (text.split('.')[1] || '').replace(/[^0-9]/g, '').length;
      const fmt = new Intl.NumberFormat('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      const start = performance.now();
      const dur = 1600;
      const tick = (now) => {
        const k = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - k, 4);
        el.textContent = fmt.format(target * eased);
        if (k < 1) requestAnimationFrame(tick); else el.textContent = text;
      };
      requestAnimationFrame(tick);
    });
  }

  // Which element scrolls the page? Horizon (2026 updates) scrolls `.page-wrapper` on
  // desktop instead of the window, so every scroll read goes through these helpers.
  function findScroller() {
    const pw = document.querySelector('.page-wrapper');
    if (pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY)) return pw;
    return null; // the window
  }
  let scroller = findScroller();
  const scrollTop = () => (scroller ? scroller.scrollTop : window.scrollY);

  // Native scrolling only. A smooth-scroll library was tried and removed: it made
  // trackpad and wheel scrolling feel delayed. Keep scrolling under the browser's control.
  window.addEventListener('resize', () => { scroller = findScroller(); });

  // Scroll-linked effects, computed once per frame.
  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  const scrubRoots = [...document.querySelectorAll('[data-ic-scrub-root]')].map((rootEl) => ({
    rootEl,
    items: [...rootEl.querySelectorAll('[data-scrub]')].map((el) => {
      const [a, b] = el.dataset.scrub.split(/\s+/).map(Number);
      return { el, a, b };
    }),
  }));
  const progressEls = [...document.querySelectorAll('[data-ic-progress]')];
  const parallaxEls = [...document.querySelectorAll('[data-ic-parallax]')];
  const heroMedia = document.querySelector('[data-ic-hero] .ic-hero__media');

  let ticking = false;
  function update() {
    ticking = false;
    const vh = window.innerHeight;

    scrubRoots.forEach(({ rootEl, items }) => {
      const r = rootEl.getBoundingClientRect();
      // 0 when the section top is 70% down the screen, 1 when its bottom reaches the bottom.
      const p = clamp01((vh * 0.7 - r.top) / (r.height - vh * 0.3));
      items.forEach(({ el, a, b }) => el.style.setProperty('--t', clamp01((p - a) / (b - a)).toFixed(4)));
    });

    progressEls.forEach((el) => {
      const r = el.getBoundingClientRect();
      const p = clamp01((vh - r.top) / (vh * 0.75));
      el.style.setProperty('--p', p.toFixed(4));
    });

    parallaxEls.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const centre = (r.top + r.height / 2 - vh / 2) / vh; // -1 .. 1
      const img = el.querySelector('img');
      if (img) img.style.setProperty('--ic-par', `${(centre * -40).toFixed(1)}px`);
    });

    if (heroMedia) {
      const y = Math.min(scrollTop(), vh);
      heroMedia.style.setProperty('--ic-drift', `${(y * 0.18).toFixed(1)}px`);
      heroMedia.querySelector('img')?.style.setProperty('--ic-drift', `${(y * 0.18).toFixed(1)}px`);
    }
  }

  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  // Capture catches scrolls from the window and from the page wrapper alike.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll);
  update();
})();
